import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Table, Badge, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faHandshake } from '@fortawesome/free-solid-svg-icons';

const ListViewTab = ({
  meetings,
  timeSlots,
  handleListMeetingClick, // Renommer handleCellClick pour ce contexte si nécessaire
  formatTime,
  formatParticipantName
}) => {
  const [activeAccordionKey, setActiveAccordionKey] = useState(null);

  const meetingsByTimeSlot = useMemo(() => {
    if (!meetings || meetings.length === 0) return {};
    const grouped = meetings.reduce((acc, m) => {
      const startTime = formatTime(m.start_time);
      if (!acc[startTime]) acc[startTime] = [];
      const companyParticipant = m.company_participants?.[0];
      const partnerParticipant = m.partner_participants?.[0];
      acc[startTime].push({
        id: m.id,
        endTime: formatTime(m.end_time),
        companyName: companyParticipant?.company?.name || 'N/A',
        companyParticipantName: formatParticipantName(companyParticipant),
        partnerName: partnerParticipant?.partner?.name || 'N/A',
        partnerParticipantName: formatParticipantName(partnerParticipant),
        is_reciprocal: m.is_reciprocal,
        rawMeeting: m // Garder l'objet meeting brut pour l'édition
      });
      return acc;
    }, {});
    // Trier les meetings dans chaque créneau par nom d'entreprise
    for (const time in grouped) {
      grouped[time].sort((a, b) => a.companyName.localeCompare(b.companyName));
    }
    return grouped;
  }, [meetings, formatTime, formatParticipantName]);

  if (Object.keys(meetingsByTimeSlot).length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        Aucun rendez-vous planifié à lister.
      </Alert>
    );
  }

  return (
    <Accordion
      activeKey={activeAccordionKey}
      onSelect={k => setActiveAccordionKey(k)}
      flush
    >
      {timeSlots
        .filter(slot => meetingsByTimeSlot[slot.start]?.length > 0) // Afficher seulement les créneaux avec des RDV
        .map((slot, index) => (
          <Accordion.Item eventKey={index.toString()} key={slot.start}>
            <Accordion.Header>
              <div className="d-flex justify-content-between w-100 align-items-center pe-2">
                <span>
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  {slot.start} - {slot.end}
                </span>
                <Badge pill bg="secondary">
                  {meetingsByTimeSlot[slot.start]?.length || 0} RDV
                </Badge>
              </div>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <Table hover striped size="sm" className="fs--1 mb-0">
                <thead className="bg-200 text-900">
                  <tr>
                    <th>Entreprise</th>
                    <th>Participant Entreprise</th>
                    <th>Partenaire</th>
                    <th>Participant Partenaire</th>
                    <th className="text-center">Réciproque</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {meetingsByTimeSlot[slot.start].map(m => (
                    <tr key={m.id}>
                      <td>{m.companyName}</td>
                      <td>{m.companyParticipantName}</td>
                      <td>{m.partnerName}</td>
                      <td>{m.partnerParticipantName}</td>
                      <td className="text-center">
                        {m.is_reciprocal ? (
                          <FontAwesomeIcon
                            icon={faHandshake}
                            title="Vœu réciproque"
                          />
                        ) : null}
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleListMeetingClick(m.rawMeeting)}
                        >
                          Modifier
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Accordion.Body>
          </Accordion.Item>
        ))}
    </Accordion>
  );
};

ListViewTab.propTypes = {
  meetings: PropTypes.array.isRequired,
  timeSlots: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired
    })
  ).isRequired,
  handleListMeetingClick: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired,
  formatParticipantName: PropTypes.func.isRequired
};

export default ListViewTab;
